import { Injectable } from '@angular/core';
import heic2any from 'heic2any';
import { LoadingService } from './loading.service';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class FileService {
  constructor(private loading: LoadingService, private httpClient: HttpClient) {}

  public async generateFile(file: any) {
    file = await this.convertToJPEGifHEIC(file);
    const b64Data = await this.convertFileToBase64(file);
    return { blob: file, image: b64Data, name: file.name };
  }

  public async generateDocumentFile(file: any) {
    file = await this.convertToJPEGifHEIC(file);
    const b64Data = await this.convertFileToBase64(file);
    return { blob: file, image: b64Data, document_name: file.name };
  }

  public async generateFilePath(filePath:any) {
    var filename = decodeURI(filePath.replace(/^.*[\\\/]/, ''));
    const b64Data = await this.convertFilePathToBase64(filePath);
    return { image: b64Data, name: filename };
  }

  public blobToFile = (theBlob: Blob, fileName: string): File => {
    let b: any = theBlob;

    //A Blob() is almost a File() - it's just missing the two properties below which we will add
    b.lastModified = new Date();
    b.name = fileName;

    //Cast to a File() type
    return <File>b;
  };

  private async convertToJPEGifHEIC(file: any) {
    if (/image\/hei(c|f)/.test(file.type)) {
      await this.loading.show();

      const jpgBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });

      await this.loading.hide();

      file = this.blobToFile(<any>jpgBlob, file.name.replace(/\.[^/.]+$/, '.jpg'));
    }
    return file;
  }

  public async convertFilePathToBase64(filePath) {
    try {
      if (!filePath) {
        throw new Error('filePath is undefined or null');
      }

      // Si ya es una URL web (capacitor://, https://localhost, content://), usar getImageBase64 directamente
      const isWebUri = typeof filePath === 'string' &&
        (filePath.startsWith('capacitor://') || filePath.startsWith('content://') || filePath.startsWith('https://localhost'));
      if (isWebUri) {
        const base64String = await this.getImageBase64(filePath);
        if (base64String && typeof base64String === 'string') {
          return base64String.includes(',') ? base64String.split(',')[1] : base64String;
        }
        throw new Error('getImageBase64 returned null or invalid value');
      }

      // Limpiar la ruta: remover prefijos file://
      let cleanPath = filePath.replace(/^file:\/\//, '');

      // Para archivos temporales (/tmp/) o rutas nativas de Android/iOS (/data/, /storage/, /var/)
      const needsNativeRead = cleanPath.includes('/tmp/') || cleanPath.includes('/storage/') ||
        cleanPath.includes('/data/') || cleanPath.includes('/var/');
      if (needsNativeRead) {
        const filename = cleanPath.replace(/^.*[/\\]/, '');

        // 1. Filesystem con ruta absoluta (más fiable en Android e iOS)
        try {
          const contents = await Filesystem.readFile({ path: cleanPath });
          const data = contents.data;
          if (typeof data === 'string' && data.length > 0) return data;
          if (data instanceof ArrayBuffer) return this.arrayBufferToBase64(data);
          if (ArrayBuffer.isView(data)) return this.arrayBufferToBase64((data as any).buffer);
        } catch { /* continuar con siguiente método */ }

        // 2. Filesystem con Directory.Cache + sólo el nombre del archivo
        //    (la cámara de Android guarda en el cache de la app)
        try {
          const contents = await Filesystem.readFile({ path: filename, directory: Directory.Cache });
          const data = contents.data;
          if (typeof data === 'string' && data.length > 0) return data;
          if (data instanceof ArrayBuffer) return this.arrayBufferToBase64(data);
          if (ArrayBuffer.isView(data)) return this.arrayBufferToBase64((data as any).buffer);
        } catch { /* continuar con siguiente método */ }

        // 3. Fetch con convertFileSrc (correcto en iOS; en Android requiere que el bridge lo soporte)
        //    iOS  → capacitor://localhost/_capacitor_file_/...
        //    Android → https://localhost/_capacitor_file_/...
        try {
          const nativePath = cleanPath.startsWith('/') ? `file://${cleanPath}` : `file:///${cleanPath}`;
          const webPath = Capacitor.convertFileSrc(nativePath);
          const base64String = await this.getImageBase64(webPath);
          if (base64String && typeof base64String === 'string') {
            return base64String.includes(',') ? base64String.split(',')[1] : base64String;
          }
        } catch { /* todos los métodos fallaron */ }

        throw new Error('Failed to read file');
      }

      // Para otras rutas, usar Filesystem
      let contents = await Filesystem.readFile({
        path: cleanPath
      });
      return contents.data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Garantiza que un documento tenga un Blob válido.
   * Si blob está vacío o corrupto pero hay originalData/base64Data, crea el blob desde base64.
   * Usado por survey y document-scan cuando el picker devuelve blob vacío tras el bridge.
   */
  public ensureDocumentBlob(doc: any): any {
    if (!doc) return doc;
    const needsBlob = !doc.blob || !(doc.blob instanceof Blob) || (doc.blob?.size === 0);
    if (needsBlob && (doc.originalData || doc.base64Data)) {
      const base64 = doc.originalData ?? doc.base64Data;
      doc.blob = this.base64ToBlob(base64, doc.type || 'application/octet-stream');
    }
    return doc;
  }

  public base64ToBlob(base64String: string, contentType: string): Blob {
    if (!base64String) {
      throw new Error('base64String is null or undefined');
    }

    // Si base64String ya tiene el prefijo data:, extraer solo la parte base64
    let base64Data: string;
    if (typeof base64String === 'string' && base64String.includes(',')) {
      base64Data = base64String.split(',')[1];
    } else {
      base64Data = base64String;
    }

    if (!base64Data) {
      throw new Error('base64Data is null or undefined after processing');
    }

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  public async getImageBase64(webPath: string): Promise<any> {
    try {
      // Convertir webPath usando Capacitor.convertFileSrc para que funcione correctamente
      const convertedPath = Capacitor.convertFileSrc(webPath);
      
      // Usar fetch para obtener el blob
      const response = await fetch(convertedPath);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      if (!blob) {
        throw new Error('Blob is null or undefined');
      }
      
      // Convertir blob a base64 usando ArrayBuffer (evita problemas con FileReader)
      const arrayBuffer = await blob.arrayBuffer();
      const base64String = this.arrayBufferToBase64(arrayBuffer);
      const dataUrl = `data:${blob.type || 'image/jpeg'};base64,${base64String}`;
      
      return dataUrl;
    } catch (error) {
      throw error;
    }
  }
  
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  public async convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        if (reader.error) {
          reject(reader.error);
        } else {
          resolve(reader.result);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.onabort = () => {
        reject(new Error('FileReader was aborted'));
      };
      
      try {
        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  }
}
